<template>
  <BlockFieldset class="project-bank-customer">
    <p v-if="isNewProject" class="text-muted">{{ $t('saveNewProjectFirst') }}</p>
    <div v-else>
      <div class="bank-section-grid bank-section-grid--paired">
        <div class="label">{{ $t('bankProspectiveCustomer') }}</div>
        <div>
          <b-form-input
            id="bankProspectiveCustomer"
            :value="project.bank.prospectiveCustomer"
            @input="setBankField('prospectiveCustomer', $event)"
            type="text"
            :placeholder="$t('bankProspectiveCustomer')"
          />
        </div>
        <div class="label">{{ $t('bankGbrStatus') }}</div>
        <div>
          <b-form-select id="bankGbrStatus" v-model="gbrStatus" :options="gbrStatusOptions" />
        </div>
        <template v-for="row in ATTACHMENT_ROWS">
          <div class="label" :key="`${row.attachmentType}-label`">{{ $t(row.labelKey) }}</div>
          <div :key="row.attachmentType">
            <BankAttachmentUpload
              :project="project"
              :attachmentType="row.attachmentType"
              @changed="reloadBlock"
            />
          </div>
        </template>
      </div>
      <div v-if="!isBlockReadOnly" class="text-right mt-2">
        <b-button variant="bg" :disabled="isSaving" @click="save">
          {{ $t('saveNamed', { name: $t('bankSectionCustomer') }) }}
        </b-button>
      </div>
    </div>
  </BlockFieldset>
</template>

<script>
import { mapActions } from 'vuex';
import BlockFieldset from '@/components/Access/BlockFieldset.vue';
import BankAttachmentUpload from '@/components/Projects/BankAttachmentUpload';
import { BANK_ATTACHMENT_TYPES, GBR_STATUS } from '@/helpers/bankAttachmentTypes';
import { AccessBlockMixin } from '@/mixins/accessBlock';
import { BlockSaveMixin } from '@/mixins/blockSave';
import { ProjectBankBlockMixin } from '@/mixins/projectBankBlock';
import { PROJECT_BANK_CUSTOMER_FIELDS } from './projectBankCustomerFragment';
import { toProjectBankCustomerInput } from './projectBankCustomerInput';

/** Documents of the section in the order of the previous tab. */
const ATTACHMENT_ROWS = [
  {
    labelKey: 'bankAttachmentBusinessRegistration',
    attachmentType: BANK_ATTACHMENT_TYPES.BANK_BUSINESS_REGISTRATION,
  },
  {
    labelKey: 'bankAttachmentGbrContract',
    attachmentType: BANK_ATTACHMENT_TYPES.BANK_GBR_CONTRACT,
  },
];

/**
 * Block project-bank-customer ("Kunde", tab Bank, see feature page projekt-bank): who becomes the customer and the GbR status, saved with the own button, and the documents of the section.
 * `project` is the block data (`Project.blocks.bankCustomer`); an upload or a delete reloads it. On
 * the unsaved project it shows only a hint. Emits `dirty` and `saved`.
 */
export default {
  name: 'ProjectBankCustomer',
  components: { BankAttachmentUpload, BlockFieldset },
  mixins: [AccessBlockMixin, BlockSaveMixin, ProjectBankBlockMixin],
  bankBlockField: 'bankCustomer',
  bankBlockFragment: PROJECT_BANK_CUSTOMER_FIELDS,
  props: {
    blockId: { type: String, required: true },
    project: { type: Object, required: true },
  },
  data() {
    return { ATTACHMENT_ROWS };
  },
  computed: {
    blockInput() {
      return toProjectBankCustomerInput(this.project);
    },
    /** A missing status shows NONE, as the tab did before. */
    gbrStatus: {
      get() {
        return this.project.bank.gbrStatus ?? GBR_STATUS.NONE;
      },
      set(value) {
        this.setBankField('gbrStatus', value);
      },
    },
    gbrStatusOptions() {
      return [
        { value: GBR_STATUS.FOUNDED, text: this.$t('bankGbrStatus_FOUNDED') },
        { value: GBR_STATUS.TO_BE_FOUNDED, text: this.$t('bankGbrStatus_TO_BE_FOUNDED') },
        { value: GBR_STATUS.NONE, text: this.$t('bankGbrStatus_NONE') },
      ];
    },
  },
  methods: {
    ...mapActions(['updateProjectBankCustomer']),
    saveBlockInput(input) {
      return this.updateProjectBankCustomer({ projectNumber: this.project.number, input });
    },
    setBankField(field, value) {
      this.$set(this.project.bank, field, value);
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../styles/bankSectionGrid';
</style>
